import {TweenLite} from 'gsap';
import {animatedScrollTo} from '../helpers';
import Animations from '../lib/animations';

export default class HistoryComponent {
  static CLASSES = {
    HISTORY: 'js-history',
    HISTORY_NO_INIT: 'history--no-init',
    OUTER_WRAPPER: 'history__outer-wrapper',
    NAV_CONTAINER: 'history__nav-container',
    NAV: 'history__nav',
    NAV_AT_TOP: 'history__nav--at-top',
    NAV_AT_BOTTOM: 'history__nav--at-bottom',
    NAV_FIXED: 'history__nav--fixed',
    NAV_LIST: 'history__nav__list',
    NAV_LINK: 'history__nav__link',
    NAV_LINK_ACTIVE: 'history__nav__link--active',
    INTRO: 'history__intro',
    TIMELINE: 'history__timeline',
    PHOTO: 'history__item__photo',
    ENTRY: 'history__entry',
    ENTRY_IN_VIEWPORT: 'history__entry--in-viewport',
    END_WRAPPER: 'history__end__wrapper'
  };

  static init($history) {
    if (!$history || $history.historyComponent) {
      return;
    }

    $history.historyComponent = new HistoryComponent($history);
  }

  static initAll() {
    let {HISTORY, HISTORY_NO_INIT} = HistoryComponent.CLASSES;

    document.querySelectorAll(`.${HISTORY}:not(.${HISTORY_NO_INIT})`).forEach(($history) => {
      HistoryComponent.init($history);
    });
  }

  constructor($history) {
    this.$history = $history;
    this.$outerWrapper = $history.querySelector(`.${HistoryComponent.CLASSES.OUTER_WRAPPER}`);
    this.$navContainer = $history.querySelector(`.${HistoryComponent.CLASSES.NAV_CONTAINER}`);
    this.$nav = $history.querySelector(`.${HistoryComponent.CLASSES.NAV}`);
    this.$navList = $history.querySelector(`.${HistoryComponent.CLASSES.NAV_LIST}`);
    this.$$navLink = $history.querySelectorAll(`.${HistoryComponent.CLASSES.NAV_LINK}`);
    this.$intro = $history.querySelector(`.${HistoryComponent.CLASSES.INTRO}`);
    this.$timeline = $history.querySelector(`.${HistoryComponent.CLASSES.TIMELINE}`);
    this.$$photo = $history.querySelectorAll(`.${HistoryComponent.CLASSES.PHOTO}`);
    this.$$entry = $history.querySelectorAll(`.${HistoryComponent.CLASSES.ENTRY}`);
    this.$endWrapper = $history.querySelector(`.${HistoryComponent.CLASSES.END_WRAPPER}`);

    this.onResizeWindow = this.onResizeWindow.bind(this);
    this.onScrollWindow = this.onScrollWindow.bind(this);
    this.onClickNavList = this.onClickNavList.bind(this);
    this.intersectEntry = this.intersectEntry.bind(this);
    this.intersectPhoto = this.intersectPhoto.bind(this);

    this.init();
  }

  init() {
    this.entryIntersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(this.intersectEntry);
      this.updateNavLink();
    });

    this.$$entry.forEach(($entry) => {
      this.entryIntersectionObserver.observe($entry);
    });

    this.photoIntersectionObserver = new IntersectionObserver(
      (photos) => {
        photos.forEach(this.intersectPhoto);
      },
      {
        rootMargin: '-100px 0px'
      }
    );

    this.$$photo.forEach(($photo) => {
      this.photoIntersectionObserver.observe($photo);
    });

    this.updateNavContainer();
    this.updateNav();

    window.addEventListener('resize', this.onResizeWindow);
    window.addEventListener('scroll', this.onScrollWindow);
    this.$navList.addEventListener('click', this.onClickNavList);

    Animations.init({
      // нужно указывать размерность для всех изменяющихся значений `from`
      // для цветов rgb(a)
      'cloud-1': {
        from: {
          transform: 'translate3d(0,0,0)'
        },
        to: {
          transform: 'translate3d(0,-20%,0)'
        },
        speed: 0.5,
        inverted: true
      },
      'cloud-2': {
        from: {
          transform: 'translate3d(0,30%,0)'
        },
        to: {
          transform: 'translate3d(0,0,0)'
        },
        speed: 0.5
      }
    });
  }

  destroy() {
    this.entryIntersectionObserver.disconnect();
    window.removeEventListener('resize', this.onResizeWindow);
    window.removeEventListener('scroll', this.onScrollWindow);
    this.$navList.removeEventListener('click', this.onClickNavList);
    this.$history.historyComponent = null;
  }

  onResizeWindow() {
    this.updateNavContainer();
    this.updateNav();
    this.updateNavLink();
  }

  onScrollWindow() {
    this.updateNav();
    this.updateNavLink();
  }

  intersectPhoto(photo) {
    let $photo = photo.target.children[0];

    if (photo.isIntersecting) {
      this.showPhoto($photo, photo.boundingClientRect);
    } else {
      this.hidePhoto($photo, photo.boundingClientRect);
    }
  }

  showPhoto($photo, boundingClientRect) {
    let state = $photo.dataset.state || 'hidden';

    if (state === 'shown') {
      return;
    }

    let direction = boundingClientRect.top <= innerHeight / 2 ? 'up' : 'down';

    if (state === 'hidden') {
      TweenLite.set($photo, {
        y: direction === 'up' ? -100 : 100,
        autoAlpha: 0
      });
    }

    $photo.dataset.state = 'showing';

    TweenLite.killTweensOf($photo);

    TweenLite.to($photo, 1, {
      y: 0,
      autoAlpha: 1,
      onComplete() {
        $photo.dataset.state = 'shown';
      }
    });
  }

  hidePhoto($photo, boundingClientRect) {
    let state = $photo.dataset.state || 'hidden';

    if (state === 'hidden') {
      return;
    }

    let direction = boundingClientRect.top >= innerHeight / 2 ? 'up' : 'down';

    $photo.dataset.state = 'hiding';

    TweenLite.killTweensOf($photo);

    TweenLite.to($photo, 1, {
      y: direction === 'up' ? 100 : -100,
      autoAlpha: 0,
      clearProps: 'all',
      onComplete() {
        $photo.dataset.state = 'hidden';
      }
    });
  }

  onClickNavList(event) {
    let $target = event.target;

    while ($target !== event.currentTarget) {
      if ($target.classList.contains(HistoryComponent.CLASSES.NAV_LINK)) {
        $target.blur();
        this.scrollToYear($target.dataset.year, true);
        event.preventDefault();
        break;
      }

      $target = $target.parentElement;
    }
  }

  intersectEntry(entry) {
    entry.target.classList.toggle(HistoryComponent.CLASSES.ENTRY_IN_VIEWPORT, entry.isIntersecting);
  }

  updatePhotos() {
    this.$$photo.forEach(($historyPhoto) => {
      let $photo = $historyPhoto.children[0];
      let rect = $historyPhoto.getBoundingClientRect();
      let photoTop = window.pageYOffset + rect.top;
      let photoHeight = rect.height;
      let photoCenter = photoTop + photoHeight / 2;

      let visibleDistance = 300;
      let animationDistance = 500;
      let offset = 565;

      let showStart = photoCenter - visibleDistance - animationDistance;
      let showEnd = photoCenter - visibleDistance;
      let hideStart = photoCenter + visibleDistance;
      let hideEnd = photoCenter + visibleDistance + animationDistance;

      let viewportCenter = window.pageYOffset + window.innerHeight / 2;
      let opacity;
      let y;

      if (viewportCenter < showStart) {
        opacity = 0;
        y = -offset;
      } else if (showStart <= viewportCenter && viewportCenter < showEnd) {
        let progress = (viewportCenter - showStart) / (showEnd - showStart);

        opacity = progress;
        y = -offset * (1 - progress);
      } else if (showEnd <= viewportCenter && viewportCenter <= hideStart) {
        opacity = 1;
        y = 0;
      } else if (hideStart < viewportCenter && viewportCenter <= hideEnd) {
        let progress = (viewportCenter - hideStart) / (hideEnd - hideStart);

        opacity = 1 - progress;
        y = offset * progress;
      } else {
        opacity = 0;
        y = offset;
      }

      $photo.style.opacity = opacity;
      TweenLite.set($photo, {y});
    });
  }

  updateNavContainer() {
    let outerWrapperTop = this.$outerWrapper.offsetTop;
    let outerWrapperHeight = this.$outerWrapper.offsetHeight;
    let introTop = this.$intro.offsetTop;
    let navHeight = this.$nav.offsetHeight;
    let navContainerTop = introTop + 5;
    let navContainerBottom = outerWrapperTop + outerWrapperHeight;

    if (this.$endWrapper) {
      let endWrapperTop = window.pageYOffset + this.$endWrapper.getBoundingClientRect().top;
      let endWrapperHeight = this.$endWrapper.offsetHeight;

      navContainerBottom -=
        endWrapperTop + endWrapperHeight + (navHeight - endWrapperHeight) / 2 + 40;
    } else {
      let timelineTop = window.pageYOffset + this.$timeline.getBoundingClientRect().top;
      let timelineHeight = this.$timeline.offsetHeight;

      navContainerBottom -= timelineTop + timelineHeight;
    }

    this.$navContainer.style.top = `${navContainerTop}px`;
    this.$navContainer.style.bottom = `${navContainerBottom}px`;
  }

  updateNav() {
    let historyTop = this.$history.offsetTop;
    let navContainerTop = this.$navContainer.offsetTop;
    let navContainerHeight = this.$navContainer.offsetHeight;
    let navHeight = this.$nav.offsetHeight;
    let start = Math.max(0, historyTop + navContainerTop + navHeight / 2 - window.innerHeight / 2);
    let end = start + navContainerHeight - navHeight;
    let navTopFixed = historyTop + navContainerTop - start;

    if (window.pageYOffset <= start) {
      this.$nav.classList.remove(HistoryComponent.CLASSES.NAV_FIXED);
      this.$nav.classList.remove(HistoryComponent.CLASSES.NAV_AT_BOTTOM);
      this.$nav.classList.add(HistoryComponent.CLASSES.NAV_AT_TOP);
      this.$nav.removeAttribute('style');
    } else if (window.pageYOffset >= end) {
      this.$nav.classList.remove(HistoryComponent.CLASSES.NAV_AT_TOP);
      this.$nav.classList.remove(HistoryComponent.CLASSES.NAV_FIXED);
      this.$nav.classList.add(HistoryComponent.CLASSES.NAV_AT_BOTTOM);
      this.$nav.removeAttribute('style');
    } else {
      this.$nav.classList.remove(HistoryComponent.CLASSES.NAV_AT_TOP);
      this.$nav.classList.remove(HistoryComponent.CLASSES.NAV_AT_BOTTOM);
      this.$nav.classList.add(HistoryComponent.CLASSES.NAV_FIXED);
      this.$nav.style.top = `${navTopFixed}px`;
    }
  }

  updateNavLink() {
    let $$entryInViewport = Array.from(this.$$entry).filter(($entry) => {
      return $entry.classList.contains(HistoryComponent.CLASSES.ENTRY_IN_VIEWPORT);
    });

    let $nearestEntry;

    if (!$$entryInViewport.length) {
      let $firstEntry = this.$$entry[0];
      let $lastEntry = this.$$entry[this.$$entry.length - 1];

      if ($firstEntry.getBoundingClientRect().top > 0) {
        // Если мы где-то в начале страницы и в области видимости нет $entry
        $nearestEntry = $firstEntry;
      } else if ($lastEntry.getBoundingClientRect().top < 0) {
        // Если мы где-то в конце страницы и в области видимости нет $entry
        $nearestEntry = $lastEntry;
      }
    } else {
      let minDistance = Infinity;

      $$entryInViewport.forEach(($entry) => {
        let rect = $entry.getBoundingClientRect();
        let entryTop = window.pageYOffset + rect.top;
        let entryHeight = rect.height;
        let viewportCenter = window.pageYOffset + window.innerHeight / 2;

        let distance = Math.min(
          Math.abs(viewportCenter - entryTop),
          Math.abs(viewportCenter - (entryTop + entryHeight))
        );

        if (distance < minDistance) {
          minDistance = distance;
          $nearestEntry = $entry;
        }
      });
    }

    if (!$nearestEntry) {
      return;
    }

    this.activateNavLink($nearestEntry.dataset.year);
  }

  activateNavLink(year) {
    let $$navLink = Array.from(this.$$navLink);

    let $prevNavLink = $$navLink.find(($navLink) => {
      return $navLink.classList.contains(HistoryComponent.CLASSES.NAV_LINK_ACTIVE);
    });

    let $nextNavLink = $$navLink.find(($navLink) => {
      return $navLink.dataset.year === year;
    });

    if ($nextNavLink === $prevNavLink) {
      return;
    }

    if ($prevNavLink) {
      $prevNavLink.classList.remove(HistoryComponent.CLASSES.NAV_LINK_ACTIVE);
    }

    $nextNavLink.classList.add(HistoryComponent.CLASSES.NAV_LINK_ACTIVE);
  }

  scrollToYear(year, animated = false) {
    // eslint-disable-next-line no-shadow
    let $entry = Array.from(this.$$entry).find(($entry) => $entry.dataset.year === year);

    if (!$entry) {
      return;
    }

    let rect = $entry.getBoundingClientRect();
    let entryTop = window.pageYOffset + rect.top;
    let entryHeight = rect.height;
    let entryCenter = entryTop + entryHeight / 2;
    let scrollY = Math.min(entryTop, entryCenter - innerHeight / 2);

    if (scrollY === window.pageYOffset) {
      return;
    }

    if (animated) {
      animatedScrollTo({
        y: scrollY
      });
    } else {
      window.scrollTo(window.pageXOffset, scrollY);
    }
  }
}
